import {
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Upload,
	type UploadFile,
} from 'antd';
import { FC, useEffect } from 'react';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useForm } from '@/libs/antd/form';
import type { Dayjs } from 'dayjs';
import type { FieldMetaMap } from '@/pages/robotic/qc-pra-pencairan/qc-pd-mapping';

export type SahamIdentityFile = {
	id?: string;
	name?: string;
	url?: string;
	format?: string;
};

export type SahamFormValues = {
	nama: string;
	kewarganegaraan: string;
	nationality: string;
	jenisIdentitas: string;
	noId: string;
	identityFile?: SahamIdentityFile;
	tanggalLahir: Dayjs | null;
	jenisKelamin: string;
	noTelepon: string;
	jabatan: string;
	alamat: string;
	kodePos: string;
	kelurahan: string;
	kecamatan: string;
	kota: string;
	rt: string;
	rw: string;
	status: string;
	isManagement: boolean;
	kepemilikanSaham: number | null;
};

type QCSahamModalProps = {
	open: boolean;
	mode: 'create' | 'edit';
	readOnly?: boolean;
	record?: Partial<SahamFormValues>;
	meta?: FieldMetaMap;
	onClose: () => void;
	onSave: (values: SahamFormValues) => void;
};

const QCSahamModal: FC<QCSahamModalProps> = ({ open, mode, readOnly, record, meta, onClose, onSave }) => {
	const { form } = useForm<SahamFormValues>();

	useEffect(() => {
		if (open && record) {
			form.setFieldsValue(record);
		}
	}, [open, record, form]);

	// Per-field control from API meta. Falls back to allow editing & required if no meta.
	const isDisabled = (name: keyof SahamFormValues, fallback = false) => {
		if (readOnly) return true;
		const m = meta?.[name as string];
		if (m) return !m.editable;
		return fallback;
	};
	const isRequired = (name: keyof SahamFormValues, fallback = false) => {
		const m = meta?.[name as string];
		return m ? m.required : fallback;
	};
	const optionsFor = (name: keyof SahamFormValues, fallback: { label: string; value: string }[]) => {
		const m = meta?.[name as string];
		// If meta loaded for this field, trust API options (even if empty — BE knows best).
		// Only use fallback when meta is entirely missing (new row, no API source).
		if (m) return m.options ?? [];
		return fallback;
	};

	const reqRule = (name: keyof SahamFormValues, label: string, fallback = false) =>
		isRequired(name, fallback) ? [{ required: true, message: `${label} wajib diisi` }] : undefined;

	// Form.Item getValueProps: hides the displayed value when it doesn't match any option,
	// so Select renders blank instead of raw "0". Form state still holds the raw value for submit.
	const blankIfUnmatched = (options: { label: string; value: string }[]) => (value: unknown) => {
		if (value == null || value === '') return { value: undefined };
		return options.some((o) => o.value === String(value)) ? { value } : { value: undefined };
	};

	// Pre-resolve option lists per field
	const kewarganegaraanOpts = optionsFor('kewarganegaraan', [
		{ label: 'WNI', value: 'WNI' },
		{ label: 'WNA', value: 'WNA' },
	]);
	const jenisIdentitasOpts = optionsFor('jenisIdentitas', [
		{ label: 'KTP', value: 'KTP' },
		{ label: 'Paspor', value: 'Paspor' },
		{ label: 'KITAS', value: 'KITAS' },
	]);
	const jenisKelaminOpts = optionsFor('jenisKelamin', [
		{ label: 'Laki-Laki', value: 'Laki-Laki' },
		{ label: 'Perempuan', value: 'Perempuan' },
		{ label: 'Company', value: 'Company' },
	]);
	const jabatanOpts = optionsFor('jabatan', [
		{ label: 'Bukan Pemilik - Komisaris Utama', value: 'bukan-pemilik-komisaris' },
		{ label: 'Bukan Pemilik - Direktur', value: 'bukan-pemilik-direktur' },
		{ label: 'Pemilik - Direktur', value: 'pemilik-direktur' },
		{ label: 'Pemilik - Anggota', value: 'pemilik-anggota' },
	]);
	const statusOpts = optionsFor('status', [
		{ label: 'Aktif', value: 'aktif' },
		{ label: 'Tidak Aktif', value: 'tidak-aktif' },
	]);

	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			form={form}
			labelCol={{ span: 10 }}
			wrapperCol={{ span: 14 }}
			disabled={readOnly}
			onFinish={(values) => {
				onSave(values);
			}}
		>
			<FleetModal
				open={open}
				width={760}
				buttonPosition='end'
				closeIcon={false}
				keyboard={false}
				maskClosable={false}
				title={
					<FleetTitle level={1} marginBottom={0}>
						{readOnly ? 'Detail Pemegang Saham' : mode === 'create' ? 'Tambah Pemegang Saham' : 'Edit Pemegang Saham'}
					</FleetTitle>
				}
				styles={{
					header: { padding: 0, border: 'none' },
					body: { paddingTop: 20 },
				}}
				onOk={() => form.submit()}
				onCancel={onClose}
				afterClose={() => form.resetFields()}
				okButtonProps={readOnly ? { style: { display: 'none' } } : undefined}
				cancelText={readOnly ? 'TUTUP' : 'CANCEL'}
			>
				<Row gutter={[40, 20]}>
					{/* Nama + Kewarganegaraan */}
					<Col span={12}>
						<Form.Item
							name='nama'
							label='Nama'
							rules={reqRule('nama', 'Nama', true)}
						>
							<Input disabled={isDisabled('nama')} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name='kewarganegaraan'
							label='Kewarganegaraan'
							rules={reqRule('kewarganegaraan', 'Kewarganegaraan')}
							getValueProps={blankIfUnmatched(kewarganegaraanOpts)}
						>
							<Select
								allowClear
								disabled={isDisabled('kewarganegaraan')}
								options={kewarganegaraanOpts}
							/>
						</Form.Item>
					</Col>

					{/* Nationality + Jenis Identitas */}
					<Col span={12}>
						<Form.Item name='nationality' label='Nationality' rules={reqRule('nationality', 'Nationality')}>
							<Input disabled={isDisabled('nationality')} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name='jenisIdentitas'
							label='Jenis Identitas'
							rules={reqRule('jenisIdentitas', 'Jenis Identitas')}
							getValueProps={blankIfUnmatched(jenisIdentitasOpts)}
						>
							<Select
								allowClear
								disabled={isDisabled('jenisIdentitas')}
								options={jenisIdentitasOpts}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					{/* No. ID + Upload Identitas */}
					<Col span={12}>
						<Form.Item name='noId' label='No. ID' rules={reqRule('noId', 'No. ID')}>
							<Input disabled={isDisabled('noId')} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name='identityFile'
							label='Upload Identitas'
							required={isRequired('identityFile')}
							getValueProps={(value) => {
								const file = value as SahamIdentityFile | undefined;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name ?? 'Dokumen',
													url: file.url,
													status: 'done',
												},
											] as UploadFile[])
										: [],
								};
							}}
						>
							<Upload
								listType='text'
								maxCount={1}
								beforeUpload={() => false}
								disabled={isDisabled('identityFile', true)}
								onPreview={(file) => {
									const url = (file as UploadFile).url;
									if (url) window.open(url, '_blank', 'noopener,noreferrer');
								}}
								showUploadList={{
									showRemoveIcon: !isDisabled('identityFile', true),
									showPreviewIcon: true,
								}}
							>
								{/* Always show placeholder input when file is empty (Upload hides children once a file is set) */}
								<Input placeholder='Pilih file...' readOnly disabled={isDisabled('identityFile', true)} />
							</Upload>
						</Form.Item>
					</Col>

					{/* Tanggal Lahir + Jenis Kelamin */}
					<Col span={12}>
						<Form.Item name='tanggalLahir' label='Tanggal Lahir' rules={reqRule('tanggalLahir', 'Tanggal Lahir')}>
							<DatePicker style={{ width: '100%' }} format='DD/MM/YYYY' disabled={isDisabled('tanggalLahir')} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name='jenisKelamin'
							label='Jenis Kelamin'
							rules={reqRule('jenisKelamin', 'Jenis Kelamin')}
							getValueProps={blankIfUnmatched(jenisKelaminOpts)}
						>
							<Select
								allowClear
								disabled={isDisabled('jenisKelamin')}
								options={jenisKelaminOpts}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					{/* No. Telepon + Jabatan */}
					<Col span={12}>
						<Form.Item name='noTelepon' label='No. Telepon' rules={reqRule('noTelepon', 'No. Telepon')}>
							<Input disabled={isDisabled('noTelepon')} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name='jabatan'
							label='Jabatan'
							rules={reqRule('jabatan', 'Jabatan')}
							getValueProps={blankIfUnmatched(jabatanOpts)}
						>
							<Select
								allowClear
								disabled={isDisabled('jabatan')}
								options={jabatanOpts}
							/>
						</Form.Item>
					</Col>

					{/* Alamat - full width */}
					<Col span={24}>
						<Form.Item
							name='alamat'
							label='Alamat'
							labelCol={{ span: 5 }}
							wrapperCol={{ span: 19 }}
							rules={reqRule('alamat', 'Alamat')}
						>
							<Input disabled={isDisabled('alamat')} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					{/* Kode Pos + Kelurahan */}
					<Col span={12}>
						<Form.Item name='kodePos' label='Kode Pos' rules={reqRule('kodePos', 'Kode Pos')}>
							<Input disabled={isDisabled('kodePos')} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='kelurahan' label='Kelurahan' rules={reqRule('kelurahan', 'Kelurahan')}>
							<Input disabled={isDisabled('kelurahan')} />
						</Form.Item>
					</Col>

					{/* Kecamatan + Kota */}
					<Col span={12}>
						<Form.Item name='kecamatan' label='Kecamatan' rules={reqRule('kecamatan', 'Kecamatan')}>
							<Input disabled={isDisabled('kecamatan')} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='kota' label='Kota' rules={reqRule('kota', 'Kota')}>
							<Input disabled={isDisabled('kota')} />
						</Form.Item>
					</Col>

					{/* RT/RW + Status */}
					<Col span={12}>
						<Form.Item label='RT/RW' required={isRequired('rt') || isRequired('rw')}>
							<Row gutter={8} align='middle'>
								<Col span={11}>
									<Form.Item name='rt' noStyle>
										<Input placeholder='RT' disabled={isDisabled('rt')} />
									</Form.Item>
								</Col>
								<Col span={2} style={{ textAlign: 'center', lineHeight: '32px' }}>
									/
								</Col>
								<Col span={11}>
									<Form.Item name='rw' noStyle>
										<Input placeholder='RW' disabled={isDisabled('rw')} />
									</Form.Item>
								</Col>
							</Row>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name='status'
							label='Status'
							rules={reqRule('status', 'Status')}
							getValueProps={blankIfUnmatched(statusOpts)}
						>
							<Select
								allowClear
								disabled={isDisabled('status')}
								options={statusOpts}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					{/* Checkbox + Kepemilikan Saham */}
					<Col span={12}>
						<Form.Item name='isManagement' valuePropName='checked' wrapperCol={{ span: 24 }}>
							<Checkbox disabled={isDisabled('isManagement')}>
								<span style={{ fontSize: 12 }}>
									Checklist kotak disamping apabila{' '}
									<strong style={{ color: '#1557BC' }}>Pengurus</strong>{' '}
									juga merupakan{' '}
									<strong style={{ color: '#1557BC' }}>Pemegang Saham</strong>
								</span>
							</Checkbox>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='kepemilikanSaham' label='Kepemilikan Saham' rules={reqRule('kepemilikanSaham', 'Kepemilikan Saham')}>
							<InputNumber
								style={{ width: '100%' }}
								addonAfter='%'
								min={0}
								max={100}
								disabled={isDisabled('kepemilikanSaham')}
							/>
						</Form.Item>
					</Col>
				</Row>
			</FleetModal>
		</Form>
	);
};

export default QCSahamModal;
